import React, { useState, useEffect, useContext } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  ListItemSecondaryAction,
  CircularProgress,
  Pagination,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { collection, query, orderBy, where, or } from 'firebase/firestore'
import { firebase_firestore, firebase_functions } from '../../../common/firebaseConfig'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import MailIcon from '@mui/icons-material/Mail'
import ShareableDialog from '../../../components/ShareableDialog'

interface MessagesTabProps {
  usermeta: any
  formatDate: (timestamp: number) => string
  currentPage: number
  setCurrentPage: (page: number) => void
  totalPages: number
  setTotalPages: (pages: number) => void
  itemsPerPage: number
}

const MessagesTab: React.FC<MessagesTabProps> = ({
  usermeta,
  formatDate,
  currentPage,
  setCurrentPage,
  totalPages,
  setTotalPages,
  itemsPerPage,
}) => {
  const theme = useTheme()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const [shareableData, setShareableData] = useState<any>(null)
  const [shareables, setShareables] = useState<any[]>([])

  const [consumeShareable, consumeShareableLoading] = useHttpsCallable(
    firebase_functions,
    'consumeShareable'
  )

  // Combined OR query for both invited and accepted shareables
  const createCombinedQuery = () => {
    return query(
      collection(firebase_firestore, `shareables`),
      or(
        where('emailsInvited', 'array-contains', usermeta.email),
        where('gcidAccepted', 'array-contains', usermeta.gcid)
      ),
      orderBy('createdAt', 'desc')
    )
  }

  const [shareablesData, loadingShareables, errorShareables] = useCollectionData(
    createCombinedQuery(),
    { snapshotListenOptions: { includeMetadataChanges: true } }
  )

  // Process and categorize shareables
  useEffect(() => {
    if (!shareablesData) {
      setShareables([])
      return
    }

    // Categorize shareables and add sort timestamps
    const processedShareables = shareablesData.map((shareable: any) => {
      const isInvited = shareable.emailsInvited?.includes(usermeta.email)
      const isAccepted = shareable.gcidAccepted?.includes(usermeta.gcid)

      let category = 'invited'
      let sortTimestamp = shareable.createdAt

      // If user is both invited and accepted, prioritize accepted status
      if (isAccepted && !isInvited) {
        category = 'accepted'
        sortTimestamp =
          shareable.gcidAcceptedMap?.[usermeta.gcid]?.acceptedAt || shareable.createdAt
      }

      return {
        ...shareable,
        category,
        sortTimestamp,
      }
    })

    // Sort: invited first (by createdAt), then accepted (by acceptedAt)
    const sortedShareables = processedShareables.sort((a: any, b: any) => {
      // First sort by category (invited first)
      if (a.category === 'invited' && b.category === 'accepted') return -1
      if (a.category === 'accepted' && b.category === 'invited') return 1

      // Within same category, sort by timestamp (newest first)
      return b.sortTimestamp - a.sortTimestamp
    })

    setShareables(sortedShareables)
  }, [shareablesData, usermeta.email, usermeta.gcid])

  // Handle pagination for shareables
  const getPaginatedShareables = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return shareables.slice(startIndex, endIndex)
  }

  // Update total pages when shareables change
  useEffect(() => {
    const totalItems = shareables.length
    const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage) || 1
    setTotalPages(calculatedTotalPages)
  }, [shareables, itemsPerPage, setTotalPages])

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value)
  }

  const handleView = async (shareable: any) => {
    const result: any = await consumeShareable({ id: shareable.linkId })
    if (result?.data?.status >= 400) {
      setSnackbarSettings({
        message: result?.data?.message
          ? result.data.message
          : `There was a problem importing the shareable onto your account!`,
        severity: 'error',
        autoHideDuration: 6000,
      })
    } else {
      setShareableData(result?.data)
    }
  }

  const names: Record<string, string> = {
    PATTERN: 'Pattern',
    BACKTEST: 'Backtest',
    ALERT: 'Alert',
  }

  // Determine the appropriate date to display based on category
  const getDisplayDate = (shareable: any) => {
    if (
      shareable.category === 'accepted' &&
      shareable.gcidAcceptedMap?.[usermeta.gcid]?.acceptedAt
    ) {
      return formatDate(shareable.gcidAcceptedMap[usermeta.gcid].acceptedAt)
    }
    return shareable.createdAt ? formatDate(shareable.createdAt) : ''
  }

  const paginatedShareables = getPaginatedShareables()

  return (
    <>
      <Card sx={{ backgroundColor: theme.palette.background.custom2 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <MailIcon /> Messages
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loadingShareables ? (
            <Typography variant="body1">Loading...</Typography>
          ) : errorShareables ? (
            <Typography variant="body1">Error: {errorShareables?.message}</Typography>
          ) : shareables.length > 0 ? (
            <>
              <List>
                {paginatedShareables.map((shareable: any, index: number) => {
                  const isUnread = shareable.category === 'invited'
                  const displayDate = getDisplayDate(shareable)

                  return (
                    <div key={shareable.linkId || index}>
                      <ListItem
                        sx={{
                          backgroundColor: isUnread ? 'action.hover' : 'transparent',
                          borderLeft: isUnread ? 3 : 0,
                          borderLeftColor: isUnread ? 'primary.main' : 'transparent',
                          pl: isUnread ? 2 : 3,
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: isUnread ? 'bold' : 'normal' }}
                              >
                                {names[shareable.type]}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: isUnread ? 'bold' : 'normal',
                                  color: 'text.secondary',
                                }}
                              >
                                {shareable.name}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Typography
                                className="ph-mask"
                                variant="body2"
                                component="span"
                                sx={{ fontWeight: isUnread ? 'medium' : 'normal' }}
                              >
                                Shared by {shareable.createdBy?.email}
                              </Typography>
                              {displayDate && (
                                <>
                                  <Box mx={0.5} display={'inline-block'}>
                                    •
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ color: 'text.secondary', display: 'inline-block' }}
                                  >
                                    {shareable.category === 'accepted'
                                      ? `Viewed ${displayDate}`
                                      : displayDate}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Button
                            variant={isUnread ? 'contained' : 'outlined'}
                            color="primary"
                            size="small"
                            onClick={() => handleView(shareable)}
                            disabled={consumeShareableLoading}
                          >
                            {consumeShareableLoading ? <CircularProgress size={20} /> : 'View'}
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < paginatedShareables.length - 1 && <Divider />}
                    </div>
                  )
                })}
              </List>
              {shareables.length > itemsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <MailIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Messages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You haven't received any shared patterns, backtests, or alerts yet.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {shareableData && (
        <ShareableDialog shareableData={shareableData} setShareableData={setShareableData} />
      )}
    </>
  )
}

export default MessagesTab
